import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import {
  browserTracingIntegration,
  captureConsoleIntegration,
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
  setTag,
  setUser
} from '@sentry/react';
import { getAnonymousUuid } from 'utils/auth';
import { isPwaUser } from 'utils/pwa';
import { env } from '../../constants';
import { isNetworkRequestEvent, scrubSensitiveData } from './utils';

export const initSentry = () => {
  if (env.PROD && env.VITE_SENTRY_DSN && env.VITE_ENV === 'production') {
    init({
      environment: env.MODE,
      dsn: env.VITE_SENTRY_DSN,
      _experiments: { enableLogs: true },
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 1.0,
      tracePropagationTargets: [
        'preprod.zenjob.io',
        /^https:\/\/pro-talent-app.zenjob\.org/,
        /pro-core\.zenjob\.com/
      ],
      ignoreErrors: [
        // This error is thrown when the user does not give consent for tracking and a page refresh is forced - preferences are still saved
        'Usercentrics [CmpController - saveConsentRemotely]: Could not save consents'
      ],
      integrations: [
        captureConsoleIntegration({ levels: ['error'] }),
        reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        }),
        browserTracingIntegration(),
        replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          mask: ['[data-sentry-mask]'],
          networkDetailAllowUrls: [
            window.location.origin,
            'https://staging-pro-core.zenjob.org',
            'https://pro-core.zenjob.com',
            'https://zj-staging.preprod.zenjob.org',
            'https://zj-live.live.zenjob.com'
          ],
          beforeAddRecordingEvent: event => {
            if (isNetworkRequestEvent(event)) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const requestFrameEvent = event as any;
              scrubSensitiveData(requestFrameEvent, 'message');
            }
            return event;
          }
        })
      ],
      beforeSend(event) {
        event.tags = {
          ...event.tags,
          online:
            typeof navigator !== 'undefined'
              ? String(navigator.onLine).toLowerCase()
              : 'unknown'
        };
        return event;
      }
    });

    setUser({ id: getAnonymousUuid() });
    setTag('isPwaUser', isPwaUser() ? 'true' : 'false');
  }
};
